/**
 * WeekCalendar Component - Weekly calendar grid with shifts
 * Mobile: Horizontal scrollable, Desktop: Full 7-column grid
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import ShiftCard from './ShiftCard';

// Day names
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function WeekCalendar({
  weekDates,
  shifts,
  loading,
  onPrevWeek,
  onNextWeek,
  onToday,
  onAddShift,
  onEditShift,
  formatDate,
  formatDateAPI,
}) {
  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Get shifts for a specific date
  const getShiftsForDate = (date) => {
    const dateStr = formatDateAPI(date);
    return shifts.filter(s => {
      const shiftDate = s.start_at ? s.start_at.split('T')[0] : null;
      return shiftDate === dateStr;
    });
  };

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardHeader className="pb-2 px-3 sm:px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button 
              variant="outline" 
              size="icon"
              className="border-slate-700 h-8 w-8 sm:h-10 sm:w-10"
              onClick={onPrevWeek}
              data-testid="prev-week-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <CardTitle className="text-white text-sm sm:text-lg" data-testid="week-range">
              <span className="hidden sm:inline">
                {formatDate(weekDates[0])} - {formatDate(weekDates[6])}, {weekDates[0].getFullYear()}
              </span>
              <span className="sm:hidden">
                {formatDate(weekDates[0])} - {formatDate(weekDates[6])}
              </span>
            </CardTitle>
            
            <Button 
              variant="outline" 
              size="icon"
              className="border-slate-700 h-8 w-8 sm:h-10 sm:w-10"
              onClick={onNextWeek}
              data-testid="next-week-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          <Button 
            variant="outline" 
            size="sm"
            className="border-slate-700 text-slate-300 h-8 sm:h-9 text-xs sm:text-sm"
            onClick={onToday}
            data-testid="today-btn"
          >
            Today
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="px-2 sm:px-6 pb-4">
        {/* Mobile: Horizontal scroll */}
        <div className="overflow-x-auto -mx-2 px-2 sm:mx-0 sm:px-0">
          <div className="flex sm:grid sm:grid-cols-7 gap-2 min-w-[700px] sm:min-w-0">
            {weekDates.map((date, index) => {
              const dayShifts = getShiftsForDate(date);
              const isCurrentDay = isToday(date);
              
              return (
                <div 
                  key={index}
                  className={`flex-shrink-0 w-[100px] sm:w-auto min-h-[160px] sm:min-h-[200px] rounded-lg border ${
                    isCurrentDay 
                      ? 'border-emerald-500 bg-emerald-500/5' 
                      : 'border-slate-700 bg-slate-800/50'
                  }`}
                  data-testid={`day-column-${index}`}
                >
                  {/* Day Header */}
                  <div className={`p-1.5 sm:p-2 border-b ${isCurrentDay ? 'border-emerald-500/30' : 'border-slate-700'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-[10px] sm:text-xs font-medium ${isCurrentDay ? 'text-emerald-400' : 'text-slate-400'}`}>
                          {DAYS[date.getDay()]}
                        </p>
                        <p className={`text-base sm:text-lg font-bold ${isCurrentDay ? 'text-emerald-400' : 'text-white'}`}>
                          {date.getDate()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 sm:h-6 sm:w-6 text-slate-500 hover:text-white"
                        onClick={() => onAddShift(date)}
                        data-testid={`add-shift-btn-${index}`}
                      >
                        <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Shifts */}
                  <div className="p-1.5 sm:p-2 space-y-1.5 sm:space-y-2">
                    {loading ? (
                      <div className="animate-pulse space-y-2">
                        <div className="h-10 sm:h-12 bg-slate-700 rounded" />
                      </div>
                    ) : dayShifts.length === 0 ? (
                      <p className="text-[10px] sm:text-xs text-slate-500 text-center py-3 sm:py-4">No shifts</p>
                    ) : (
                      dayShifts.map(shift => (
                        <ShiftCard 
                          key={shift.id}
                          shift={shift}
                          onClick={onEditShift}
                          compact={true}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Mobile scroll hint */}
        <p className="text-[10px] text-slate-500 text-center mt-2 sm:hidden">
          ← Swipe to see all days →
        </p>
      </CardContent>
    </Card>
  );
}
